import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, ExternalLink, Github, Star } from 'lucide-react';
import SectionHeader from './SectionHeader';
import './Projects.css';

// Project screenshots (browser chrome cropped off, converted to WebP)
import neha from '../images/project/neha.webp';
import fixitops from '../images/project/fixitops.webp';
import arena from '../images/project/arena.webp';
import ofos from '../images/project/ofos.webp';
import osma from '../images/project/osma.webp';
import exp from '../images/project/exp.webp';

// Showcase: a numbered list on the left, one large readable preview on the right.
// AI work comes first (it is the focus), then the full-stack apps.
const groups = [
  {
    id: 'ai',
    title: 'AI & Agents',
    blurb: 'LLM-powered and multi-agent systems',
    projects: [
      {
        id: 'neha',
        featured: true,
        name: 'NEHA',
        kicker: 'AI Legal Research Assistant',
        description:
          'AI-powered legal research platform with chat assistance, multi-party debate simulation for case analysis, legal news aggregation, and RAG-based document Q&A.',
        image: neha,
        github: 'https://github.com/phanicharan04/Neha_ML_Backend',
        live: 'https://neha-ai.vercel.app/',
        technologies: [
          'React', 'TypeScript', 'Node.js', 'Express', 'MongoDB', 'Python',
          'FastAPI', 'LangChain', 'Groq API', 'JWT Authentication', 'Vite'
        ]
      },
      {
        id: 'fixitops',
        name: 'FixitOps',
        kicker: 'AI Automated Incident Response',
        description:
          'Multi-agent DevOps assistant built with LangGraph. Four agents work together on automated root-cause analysis and remediation.',
        image: fixitops,
        github: 'https://github.com/PHANICHARAN04/FixitOps_LLM_Backend',
        live: null,
        technologies: ['Python', 'FastAPI', 'LangGraph', 'ChromaDB', 'Groq API']
      },
      {
        id: 'arena',
        name: 'Arena',
        kicker: 'Multi-Framework Agent Debate',
        description:
          'Rating platform that compares LangGraph, AutoGen, and Semantic Kernel by running parallel multi-agent debates.',
        image: arena,
        github: 'https://github.com/phanicharan04/Arena_LLM_Backend',
        live: null,
        technologies: ['Python', 'FastAPI', 'LangGraph', 'AutoGen', 'Semantic Kernel', 'Groq API']
      }
    ]
  },
  {
    id: 'fullstack',
    title: 'Full-Stack Apps',
    blurb: 'End-to-end web applications',
    projects: [
      {
        id: 'haveit',
        name: 'HaveIt',
        kicker: 'Online Food Ordering System',
        description:
          'Online food platform with three modules for seamless ordering, plus a streamlined registration flow for restaurant owners.',
        image: ofos,
        github: 'https://github.com/phanicharan04/OnlineFoodOrderingSystem/tree/main/OFOS',
        live: null,
        technologies: ['Spring Boot', 'Spring Data JPA', 'Java', 'MySQL', 'HTML', 'CSS', 'Stripe']
      },
      {
        id: 'hordetalk',
        name: 'HordeTalk',
        kicker: 'Online Social Media Application',
        description:
          'Idea-sharing platform where users post innovative ideas, connect with like-minded people, and collaborate.',
        image: osma,
        github: 'https://github.com/phanicharan04/HordeTalk',
        live: 'https://hordetalk.vercel.app/',
        technologies: ['React JS', 'Express JS (Node JS)', 'MongoDB', 'JWT']
      },
      {
        id: 'expense',
        name: 'Expense Tracker',
        kicker: 'Budget & Expense Management',
        description:
          'Budget and expense tracker with graphical reports and threshold alerts to help manage finances.',
        image: exp,
        github: 'https://github.com/phanicharan04/Expense-Tracker',
        live: null,
        technologies: ['React', 'JavaScript', 'CSS', 'Express JS (Node JS)', 'MongoDB']
      }
    ]
  }
];

// Hover intent: the preview only switches once the pointer has rested on a row this long.
const HOVER_INTENT_MS = 110;
const pad = (n) => String(n).padStart(2, '0');

const Projects = () => {
  const reduceMotion = useReducedMotion();

  // Flatten for numbering and keyboard navigation.
  const projects = useMemo(() => groups.flatMap((g) => g.projects), []);

  const [activeIndex, setActiveIndex] = useState(0);
  const active = projects[activeIndex];
  const tabRefs = useRef([]);
  const hoverTimer = useRef(null);

  // Warm the cache so switching projects never shows an empty screenshot.
  useEffect(() => {
    projects.forEach((p) => {
      const img = new Image();
      img.src = p.image;
    });
  }, [projects]);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  // One entrance pattern for the whole page; disabled for reduced-motion users.
  const rise = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.15 },
          transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }
        };

  const select = (index, { focus = false } = {}) => {
    setActiveIndex(index);
    if (focus) tabRefs.current[index]?.focus();
  };

  // Called on every pointer move over a row, which restarts the timer. The preview therefore
  // switches only when the pointer pauses, so travelling across other rows (for example from the
  // list to the "Live demo" button) never swaps the preview out from under you.
  const hoverIntent = (index) => {
    clearTimeout(hoverTimer.current);
    if (index === activeIndex) return;
    hoverTimer.current = setTimeout(() => setActiveIndex(index), HOVER_INTENT_MS);
  };

  const cancelHover = () => clearTimeout(hoverTimer.current);

  const onKeyDown = (e) => {
    const last = projects.length - 1;
    const keys = {
      ArrowDown: Math.min(activeIndex + 1, last),
      ArrowRight: Math.min(activeIndex + 1, last),
      ArrowUp: Math.max(activeIndex - 1, 0),
      ArrowLeft: Math.max(activeIndex - 1, 0),
      Home: 0,
      End: last
    };
    if (e.key in keys) {
      e.preventDefault();
      select(keys[e.key], { focus: true });
    }
  };

  let runningIndex = -1;

  return (
    <section className="projects-section" id="projects">
      <div className="projects-container">
        <SectionHeader
          eyebrow="Selected work"
          lead="Featured"
          accent="Projects"
          subtitle="Things I've built, from multi-agent AI systems to full-stack web apps."
          {...rise()}
        />

        <motion.div className="pj-showcase" {...rise(0.05)}>
          {/* ---------- Project list ---------- */}
          <div
            className="pj-list"
            role="tablist"
            aria-orientation="vertical"
            aria-label="Projects"
            onKeyDown={onKeyDown}
            onMouseLeave={cancelHover}
          >
            {groups.map((group) => (
              <div key={group.id} className="pj-list-group">
                <p className="pj-list-label">{group.title}</p>

                {group.projects.map((project) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  const isActive = index === activeIndex;

                  return (
                    <button
                      key={project.id}
                      type="button"
                      role="tab"
                      id={`pj-tab-${project.id}`}
                      aria-selected={isActive}
                      aria-controls="pj-panel"
                      tabIndex={isActive ? 0 : -1}
                      ref={(el) => { tabRefs.current[index] = el; }}
                      className={`pj-row ${isActive ? 'is-active' : ''}`}
                      onClick={() => select(index)}
                      onFocus={() => select(index)}
                      onMouseEnter={() => hoverIntent(index)}
                      onMouseMove={() => hoverIntent(index)}
                      onMouseLeave={cancelHover}
                    >
                      {isActive && !reduceMotion && (
                        <motion.span
                          layoutId="pj-indicator"
                          className="pj-indicator"
                          transition={{ type: 'spring', stiffness: 420, damping: 38 }}
                        />
                      )}
                      {isActive && reduceMotion && <span className="pj-indicator" />}
                      <span className="pj-row-num" aria-hidden="true">{pad(index + 1)}</span>
                      <span className="pj-row-text">
                        <span className="pj-row-name">{project.name}</span>
                        <span className="pj-row-kicker">{project.kicker}</span>
                      </span>
                      <ArrowUpRight className="pj-row-arrow" size={20} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* ---------- Large preview ---------- */}
          <div className="pj-stage" id="pj-panel" role="tabpanel" aria-labelledby={`pj-tab-${active.id}`}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.article
                key={active.id}
                className={`pj-detail ${active.featured ? 'is-featured' : ''}`}
                initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="pj-media">
                  <div className="pj-window">
                    <div className="pj-bar" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                      {active.live && (
                        <b className="pj-live">
                          <i />
                          Live
                        </b>
                      )}
                    </div>
                    <div className="pj-shot">
                      <img src={active.image} alt={`${active.name} screenshot`} />
                    </div>
                  </div>
                </div>

                <div className="pj-body">
                  <div className="pj-title-row">
                    <h4 className="pj-title">{active.name}</h4>
                    {active.featured && (
                      <span className="pj-flag">
                        <Star size={12} aria-hidden="true" />
                        Flagship
                      </span>
                    )}
                  </div>
                  <p className="pj-kicker">{active.kicker}</p>
                  <p className="pj-desc">{active.description}</p>

                  <ul className="pj-tech" aria-label={`${active.name} technologies`}>
                    {active.technologies.map((tech) => (
                      <li key={tech}>{tech}</li>
                    ))}
                  </ul>

                  {(active.live || active.github) && (
                    <div className="pj-links">
                      {active.live && (
                        <a
                          href={active.live}
                          className="pj-link pj-link-live"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${active.name} live demo`}
                        >
                          Live demo
                          <ExternalLink size={15} />
                        </a>
                      )}
                      {active.github && (
                        <a
                          href={active.github}
                          className="pj-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${active.name} source code on GitHub`}
                        >
                          <Github size={15} />
                          Code
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
